import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

export interface CompareProduct {
  activities: string[];
  aggregateRating: number | null;
  availableWeights: string[];
  brand: string | null;
  category: string | null;
  cbdDosageMg: number | null;
  feelings: string[];
  flavors: string[];
  id: number;
  imageUrl: string;
  kind: string;
  name: string;
  originalPrice: number | null;
  percentCbd: number | null;
  percentThc: number | null;
  price: number | null;
  reviewCount: number | null;
  thcDosageMg: number | null;
  /** PDP url — when present, modal product links render as real anchors */
  url?: string | null;
  weight: string | null;
}

export interface CompareHandlers {
  addToCart?: () => void;
  view?: () => void;
}

export interface CompareContextValue {
  clearAll: () => void;
  closeModal: () => void;
  /** Close the modal in response to a user dismissal (fires onCloseModal) */
  dismissModal: () => void;
  enabled: boolean;
  isActive: boolean;
  isModalOpen: boolean;
  isSelected: (id: number) => boolean;
  openModal: () => void;
  productCount: number;
  products: CompareProduct[];
  removeProduct: (id: number) => void;
  setActive: (active: boolean) => void;
  toggleActive: () => void;
  toggleProduct: (
    product: CompareProduct,
    handlers?: CompareHandlers
  ) => boolean;
  triggerAddToCart: (id: number) => void;
  triggerView: (id: number) => void;
}

export const MAX_COMPARE = 5;

const defaultCompareContext: CompareContextValue = {
  products: [],
  productCount: 0,
  enabled: false,
  isActive: false,
  isSelected: () => false,
  toggleProduct: () => false,
  removeProduct: () => {},
  clearAll: () => {},
  isModalOpen: false,
  openModal: () => {},
  closeModal: () => {},
  dismissModal: () => {},
  setActive: () => {},
  toggleActive: () => {},
  triggerAddToCart: () => {},
  triggerView: () => {},
};

const CompareContext = createContext<CompareContextValue | undefined>(
  undefined
);

type CompareAction =
  | { product: CompareProduct; type: 'ADD_PRODUCT' }
  | { id: number; type: 'REMOVE_PRODUCT' }
  | { type: 'CLEAR_ALL' };

function compareReducer(
  state: CompareProduct[],
  action: CompareAction
): CompareProduct[] {
  switch (action.type) {
    case 'ADD_PRODUCT': {
      if (state.length >= MAX_COMPARE) return state;
      if (state.some((p) => p.id === action.product.id)) return state;
      return [...state, action.product];
    }
    case 'REMOVE_PRODUCT':
      return state.filter((p) => p.id !== action.id);
    case 'CLEAR_ALL':
      return [];
    default:
      return state;
  }
}

interface CompareProviderProps {
  children: ReactNode;
  enabled?: boolean;
  onActiveChange?: (isActive: boolean) => void;
  /** Called when the user cancels the comparison via "Clear all" */
  onClearAll?: (productCount: number) => void;
  /** Called when the user dismisses the compare modal (close button, overlay, esc) */
  onCloseModal?: (productCount: number) => void;
  /** Called when the user opens the compare modal */
  onOpenModal?: (productCount: number) => void;
}

export const CompareProvider = ({
  children,
  enabled = true,
  onActiveChange,
  onClearAll,
  onCloseModal,
  onOpenModal,
}: CompareProviderProps) => {
  const [products, dispatch] = useReducer(compareReducer, []);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const onActiveChangeRef = useRef(onActiveChange);
  onActiveChangeRef.current = onActiveChange;
  const onClearAllRef = useRef(onClearAll);
  onClearAllRef.current = onClearAll;
  const onCloseModalRef = useRef(onCloseModal);
  onCloseModalRef.current = onCloseModal;
  const onOpenModalRef = useRef(onOpenModal);
  onOpenModalRef.current = onOpenModal;

  const handlersRef = useRef<Map<number, CompareHandlers>>(new Map());

  const productCount = products.length;

  const isSelected = useCallback(
    (id: number) => products.some((p) => p.id === id),
    [products]
  );

  const toggleProduct = useCallback(
    (product: CompareProduct, handlers?: CompareHandlers): boolean => {
      if (products.some((p) => p.id === product.id)) {
        handlersRef.current.delete(product.id);
        dispatch({ type: 'REMOVE_PRODUCT', id: product.id });
        return true;
      }
      if (products.length >= MAX_COMPARE) {
        return false;
      }
      if (handlers) handlersRef.current.set(product.id, handlers);
      dispatch({ type: 'ADD_PRODUCT', product });
      return true;
    },
    [products]
  );

  const removeProduct = useCallback((id: number) => {
    handlersRef.current.delete(id);
    dispatch({ type: 'REMOVE_PRODUCT', id });
  }, []);

  const clearAll = useCallback(() => {
    onClearAllRef.current?.(productCount);
    handlersRef.current.clear();
    dispatch({ type: 'CLEAR_ALL' });
    setIsModalOpen(false);
  }, [productCount]);

  const triggerAddToCart = useCallback((id: number) => {
    handlersRef.current.get(id)?.addToCart?.();
  }, []);

  const triggerView = useCallback((id: number) => {
    handlersRef.current.get(id)?.view?.();
  }, []);

  const openModal = useCallback(() => {
    onOpenModalRef.current?.(productCount);
    setIsModalOpen(true);
  }, [productCount]);
  const closeModal = useCallback(() => setIsModalOpen(false), []);
  const dismissModal = useCallback(() => {
    onCloseModalRef.current?.(productCount);
    setIsModalOpen(false);
  }, [productCount]);

  const setActive = useCallback((active: boolean) => {
    setIsActive((prev) => {
      if (prev !== active) onActiveChangeRef.current?.(active);
      return active;
    });
  }, []);

  const toggleActive = useCallback(() => {
    setIsActive((prev) => {
      const next = !prev;
      onActiveChangeRef.current?.(next);
      return next;
    });
  }, []);

  const value = useMemo<CompareContextValue>(
    () =>
      enabled
        ? {
            products,
            productCount: products.length,
            enabled: true,
            isActive,
            isSelected,
            toggleProduct,
            removeProduct,
            clearAll,
            isModalOpen,
            openModal,
            closeModal,
            dismissModal,
            setActive,
            toggleActive,
            triggerAddToCart,
            triggerView,
          }
        : defaultCompareContext,
    [
      enabled,
      products,
      isActive,
      isSelected,
      toggleProduct,
      removeProduct,
      clearAll,
      isModalOpen,
      openModal,
      closeModal,
      dismissModal,
      setActive,
      toggleActive,
      triggerAddToCart,
      triggerView,
    ]
  );

  return (
    <CompareContext.Provider value={value}>{children}</CompareContext.Provider>
  );
};

export const useCompare = (): CompareContextValue => {
  const context = useContext(CompareContext);
  if (context === undefined) {
    if (process.env['NODE_ENV'] === 'development') {
      // eslint-disable-next-line no-console
      console.warn(
        'useCompare was called outside of <CompareProducts> (CompareProvider). Compare features will be inert.'
      );
    }
    return defaultCompareContext;
  }
  return context;
};
